import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Container, FormControlLabel, Paper, Stack, Switch, Tab, Tabs, TextField, Typography, MenuItem } from '@mui/material';
import { Link } from 'react-router-dom';
import NodeAPI from '../api';
import { useAuth } from '../context/AuthContext';

const message = (error) => error.response?.data?.error || error.response?.data?.msg || 'Request failed';

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'user' });
  const [newCategory, setNewCategory] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [userResponse, categoryResponse] = await Promise.all([
        NodeAPI.get('/admin/users'), NodeAPI.get('/admin/categories')
      ]);
      setUsers(userResponse.data);
      setCategories(categoryResponse.data);
      setError('');
    } catch (err) { setError(message(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (action, success) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      await load();
      setNotice(success);
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  };

  const createUser = (event) => {
    event.preventDefault();
    run(async () => {
      await NodeAPI.post('/admin/users', newUser);
      setNewUser({ username: '', email: '', password: '', role: 'user' });
    }, 'User created');
  };
  const updateUser = (id, changes) => run(() => NodeAPI.patch(`/admin/users/${id}`, changes), 'User updated');
  const deleteUser = (row) => {
    if (window.confirm(`Delete ${row.username} and all their quizzes? This cannot be undone.`)) {
      run(() => NodeAPI.delete(`/admin/users/${row.id}`), 'User deleted');
    }
  };
  const createCategory = (event) => {
    event.preventDefault();
    run(async () => {
      await NodeAPI.post('/admin/categories', { name: newCategory });
      setNewCategory('');
    }, 'Category created');
  };
  const renameCategory = (row) => {
    const name = window.prompt('New category name', row.name);
    if (name !== null && name.trim() && name.trim() !== row.name) {
      run(() => NodeAPI.patch(`/admin/categories/${row.id}`, { name: name.trim() }), 'Category renamed');
    }
  };
  const deleteCategory = (row) => {
    if (window.confirm(`Delete category ${row.name}?`)) {
      run(() => NodeAPI.delete(`/admin/categories/${row.id}`), 'Category deleted');
    }
  };

  return <Container maxWidth="lg" sx={{ py: 4 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4">Admin</Typography>
      <Button component={Link} to="/categories">Back to quizzes</Button>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
    <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
      <Tab label={`Users (${users.length})`} />
      <Tab label={`Categories (${categories.length})`} />
    </Tabs>
    {tab === 0 && <Stack spacing={3}>
      <Paper component="form" onSubmit={createUser} sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>Create user</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField required label="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} />
          <TextField required type="email" label="Email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
          <TextField required type="password" label="Password" inputProps={{ minLength: 8 }} value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
          <TextField select label="Role" value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} sx={{ minWidth: 110 }}>
            <MenuItem value="user">User</MenuItem><MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <Button type="submit" variant="contained" disabled={busy}>Create</Button>
        </Stack>
      </Paper>
      {users.map(row => <Paper key={row.id} sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box sx={{ flexGrow: 1 }}><Typography fontWeight="bold">{row.username}</Typography><Typography color="text.secondary">{row.email} · ID {row.id}</Typography></Box>
          <TextField select size="small" label="Role" value={row.role} disabled={busy || row.id === user?.id} onChange={e => updateUser(row.id, { role: e.target.value })} sx={{ minWidth: 110 }}>
            <MenuItem value="user">User</MenuItem><MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <FormControlLabel control={<Switch checked={Boolean(row.isVerified)} disabled={busy} onChange={e => updateUser(row.id, { isVerified: e.target.checked })} />} label="Verified" />
          <Button disabled={busy} onClick={() => {
            const username = window.prompt('Username', row.username);
            if (username !== null && username.trim() && username.trim() !== row.username) updateUser(row.id, { username: username.trim() });
          }}>Rename</Button>
          <Button disabled={busy} onClick={() => {
            const email = window.prompt('Email', row.email);
            if (email !== null && email.trim() && email.trim() !== row.email) updateUser(row.id, { email: email.trim() });
          }}>Email</Button>
          <Button color="error" disabled={busy || row.id === user?.id} onClick={() => deleteUser(row)}>Delete</Button>
        </Stack>
      </Paper>)}
    </Stack>}
    {tab === 1 && <Stack spacing={2}>
      <Paper component="form" onSubmit={createCategory} sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>Create category</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField required label="Category name" value={newCategory} onChange={e => setNewCategory(e.target.value)} sx={{ flexGrow: 1 }} />
          <Button type="submit" variant="contained" disabled={busy}>Create</Button>
        </Stack>
      </Paper>
      {categories.map(row => <Paper key={row.id} sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ flexGrow: 1 }}><Typography fontWeight="bold">{row.name}</Typography><Typography color="text.secondary">{row.topicCount} quizzes</Typography></Box>
          <Button disabled={busy} onClick={() => renameCategory(row)}>Rename</Button>
          <Button color="error" disabled={busy || row.topicCount > 0} onClick={() => deleteCategory(row)}>Delete</Button>
        </Stack>
      </Paper>)}
    </Stack>}
  </Container>;
}
